import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown } from "lucide-react";
import { PLANS, PAID_PLAN_TIERS } from "@vms/tubeforge-shared";
import { SEOHead, faqJsonLd } from "@/components/seo/SEOHead.tsx";
import { billingApi } from "@/api/billing.ts";
import { usd, shownMaxLength, type PlanPrice } from "@/pages/billing/plan-price.ts";

const T = {
  ink: "#0a0a0a", inkSoft: "#6b6b6b", rule: "#e9e6df",
  paper: "#faf8f3", paper2: "#f3efe5", accent: "#ff5b1f",
};

const PRICING_FAQ = [
  { q: "Can I switch plans any time?", a: "Yes. Upgrade or downgrade from the billing portal. Upgrades are prorated; downgrades take effect at the end of your current billing cycle." },
  { q: "What happens to my videos if I cancel?", a: "Your videos and MP4 files remain in your account. You just can't create new ones until you resubscribe." },
  { q: "Do credits roll over?", a: "No. Video credits reset each billing cycle. Unused credits do not carry over." },
  { q: "Is there an annual discount?", a: "Yes: 10% off when you pay annually." },
  { q: "What happens if a render fails?", a: "Your credit is automatically refunded. You can re-render from the storyboard without regenerating scripts or images." },
];

const FEATURES_TABLE: Array<{ label: string; key: string; format: (v: any) => string }> = [
  { label: "Videos per month", key: "videosPerMonth", format: (v: number) => String(v) },
  { label: "Max video length", key: "maxVideoLengthSeconds", format: (v: number) => shownMaxLength(v) ?? "—" },
  { label: "Image model", key: "imageModel", format: (v: string) => v.replace("flux-", "Flux ").replace(/^\w/, (c: string) => c.toUpperCase()) },
  { label: "Voice provider", key: "ttsProvider", format: (v: string) => v === "openai" ? "OpenAI TTS" : v === "elevenlabs" ? "ElevenLabs" : "ElevenLabs Multi" },
  { label: "Regens per segment", key: "regensPerSegment", format: (v: number) => v >= 999 ? "Unlimited" : String(v) },
  { label: "YouTube channels", key: "maxConnectedChannels", format: (v: number) => v === 0 ? "—" : String(v) },
  { label: "YouTube publish", key: "allowYoutubePublish", format: (v: boolean) => v ? "✓" : "—" },
  { label: "Scheduled publish", key: "allowScheduledPublish", format: (v: boolean) => v ? "✓" : "—" },
  { label: "Active series", key: "maxActiveSeries", format: (v: number) => v === 0 ? "—" : String(v) },
  { label: "Team seats", key: "teamSeats", format: (v: number) => String(v) },
];

export function PricingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  // (session 10, P2; owner 2026-10-08) the paid plans' prices are the CONFIGURED ones (GET /billing/plans, from Stripe),
  //  not constants in this page. Until they arrive, or if they can't be read, a paid card shows no figure.
  const [prices, setPrices] = useState<Partial<Record<string, PlanPrice>>>({});
  useEffect(() => {
    billingApi.plans().then((r) => setPrices(Object.fromEntries(r.plans.map((p) => [p.tier, p.price])))).catch(() => setPrices({}));  // catch-reason: no price is shown ("—"), never a guessed one
  }, []);

  return (
    <>
      <SEOHead
        title="AI Video Creator Pricing — Free to Start"
        description={`TubeForge plans start free. Starter for ${PLANS.starter.videosPerMonth} videos a month, Pro for ${PLANS.pro.videosPerMonth}, Agency for ${PLANS.agency.videosPerMonth}. No credit card required to start.`}
        path="/pricing"
        jsonLd={faqJsonLd(PRICING_FAQ)}
      />

      {/* Hero */}
      <section className="py-16 md:py-24 text-center" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <p className="text-[13px] font-semibold uppercase tracking-widest mb-4" style={{ color: T.accent }}>
            Pricing
          </p>
          <h1
            className="text-3xl md:text-5xl font-bold leading-tight mb-5"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.03em" }}
          >
            Simple, transparent pricing
          </h1>
          <p className="text-[16px] md:text-[18px] leading-relaxed max-w-xl mx-auto" style={{ color: T.inkSoft }}>
            Start free with 2 videos. Upgrade when you need more capacity, better image models, or YouTube publishing.
          </p>
        </div>
      </section>

      {/* Plan cards */}
      <section className="py-4 md:py-8" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {(["free", ...PAID_PLAN_TIERS] as const).map((tier) => {
              const plan = PLANS[tier];
              const isPro = tier === "pro";
              return (
                <div
                  key={tier}
                  className="rounded-2xl p-6 flex flex-col"
                  style={{
                    backgroundColor: isPro ? T.accent : "#fff",
                    border: isPro ? "none" : `1px solid ${T.rule}`,
                    color: isPro ? "#fff" : T.ink,
                    boxShadow: isPro ? "0 8px 32px rgba(255,91,31,0.18)" : undefined,
                  }}
                >
                  {isPro && (
                    <span className="text-[11px] font-semibold uppercase tracking-widest mb-3 opacity-80">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-[15px] font-bold mb-1">{plan.displayName}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold" style={{ letterSpacing: "-0.02em" }}>
                      {tier === "free" ? "$0" : prices[tier] ? usd(prices[tier]!.monthlyUsd) : "—"}
                    </span>
                    {tier !== "free" && prices[tier] && (
                      <span className="text-[13px] opacity-60">/mo</span>
                    )}
                  </div>
                  <ul className="space-y-2.5 mb-6 flex-1">
                    <Li isPro={isPro}>{plan.videosPerMonth} videos/month</Li>
                    {shownMaxLength(plan.maxVideoLengthSeconds) && <Li isPro={isPro}>Up to {shownMaxLength(plan.maxVideoLengthSeconds)} each</Li>}
                    <Li isPro={isPro}>{plan.imageModel.replace("flux-", "Flux ")}</Li>
                    <Li isPro={isPro}>
                      {plan.ttsProvider === "openai" ? "OpenAI TTS" : plan.ttsProvider === "elevenlabs" ? "ElevenLabs" : "ElevenLabs Multilingual"}
                    </Li>
                    {plan.allowYoutubePublish && <Li isPro={isPro}>YouTube publish</Li>}
                    {plan.allowScheduledPublish && <Li isPro={isPro}>Scheduled publishing</Li>}
                    {plan.teamSeats > 1 && <Li isPro={isPro}>{plan.teamSeats} team seats</Li>}
                  </ul>
                  <Link
                    to={tier === "free" ? "/signup" : "/subscribe"}
                    className="block text-center text-[14px] font-semibold py-3 rounded-xl transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: isPro ? "#fff" : T.accent,
                      color: isPro ? T.accent : "#fff",
                    }}
                  >
                    {tier === "free" ? "Start free" : `Choose ${plan.displayName}`}
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Feature comparison table */}
      <section className="py-16 md:py-20" style={{ backgroundColor: T.paper2 }}>
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          <h2
            className="text-2xl md:text-3xl font-bold mb-8 text-center"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Full comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${T.rule}` }}>
                  <th className="text-left py-3 pr-4 font-semibold" style={{ color: T.inkSoft }}>Feature</th>
                  {(["free", ...PAID_PLAN_TIERS] as const).map((tier) => (
                    <th key={tier} className="text-center py-3 px-4 font-semibold" style={{ color: tier === "pro" ? T.accent : T.ink }}>
                      {PLANS[tier].displayName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES_TABLE.map((row) => (
                  <tr key={row.key} style={{ borderBottom: `1px solid ${T.rule}` }}>
                    <td className="py-3 pr-4 font-medium">{row.label}</td>
                    {(["free", ...PAID_PLAN_TIERS] as const).map((tier) => (
                      <td key={tier} className="text-center py-3 px-4" style={{ color: T.inkSoft }}>
                        {row.format((PLANS[tier] as Record<string, any>)[row.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="py-16 md:py-20" style={{ backgroundColor: T.paper }}>
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <h2
            className="text-2xl md:text-3xl font-bold mb-8 text-center"
            style={{ fontFamily: '"Bricolage Grotesque", "Inter", system-ui', letterSpacing: "-0.02em" }}
          >
            Pricing FAQ
          </h2>
          <div className="space-y-3">
            {PRICING_FAQ.map((item, i) => (
              <div key={i} className="rounded-xl" style={{ border: `1px solid ${T.rule}`, backgroundColor: "#fff" }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                  style={{ background: "transparent", border: 0, cursor: "pointer", fontFamily: "inherit", color: T.ink }}
                >
                  <span className="text-[14px] font-semibold pr-4">{item.q}</span>
                  <ChevronDown
                    className="w-5 h-5 flex-shrink-0 transition-transform"
                    style={{ color: T.inkSoft, transform: openFaq === i ? "rotate(180deg)" : "none" }}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4">
                    <p className="text-[14px] leading-relaxed" style={{ color: T.inkSoft }}>
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function Li({ children, isPro }: { children: React.ReactNode; isPro: boolean }) {
  return (
    <li className="flex items-start gap-2.5 text-[13px]">
      <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ opacity: isPro ? 0.8 : 1, color: isPro ? "#fff" : T.accent }} />
      <span>{children}</span>
    </li>
  );
}
